import { BaseModel } from '../../../models/base-model'
import { Task } from './../'

import { ICloneable } from '../../../core/models'
import { ObjectHelper, TreeUtils } from '../../../helpers'

export class PathNode extends BaseModel implements ICloneable<PathNode> {
  public title: string = ''
  public description: string
  public nodeType: string
  public pathNodes: PathNode[] = []
  public tasks: Task[] = []
  private _objectHelper: ObjectHelper
  private _treeUtils: TreeUtils

  public constructor() {
    super()
    this._objectHelper = new ObjectHelper()
    this._treeUtils = new TreeUtils()
  }

  public getIcon(): string {
    // TODO: Use Utils' "dashCase()" here
    const normalizedTitle = this.getNormalizedTitle()
    return `icon-${normalizedTitle}`
  }

  public getChildNode(id: string): PathNode {
    return this.pathNodes.find((childPathNode) => childPathNode.id === id)
  }

  // TODO [GitHub Issue]: Define an iteratore to iterate over all tasks, which might be more efficient
  public getAllTasks(): Task[] {
    return this._treeUtils.reduce(this, 'pathNodes', (allTasks, childNode) => {
      Array.prototype.push.apply(allTasks, Array.isArray(childNode.tasks) ? childNode.tasks : [])
      return allTasks
    }, [].concat(this.tasks))
  }

  public getAllTasksAndSubtasks(): Task[] {
    // This function duplicates part of the `getAllTasks` function to avoid iterating over all path nodes twice
    // (for performance reasons)
    return this._treeUtils.reduce(this, 'pathNodes', (allTasksAndSubtasks, childNode) => {
      const childTasks = Array.isArray(childNode.tasks) ? childNode.tasks : []
      const childTasksAndSubtasks = childTasks.reduce((_childTasksAndSubtasks, _childNode) => {
        Array.prototype.push.apply(_childTasksAndSubtasks, _childNode.getAllChildTasks())
        return _childTasksAndSubtasks
      }, [].concat(childTasks))
      Array.prototype.push.apply(allTasksAndSubtasks, childTasksAndSubtasks)
      return allTasksAndSubtasks
    }, [].concat(this.tasks))
  }

  public getOptionalTasks(): Task[] {
    return this.tasks.filter((task) => task.optional)
  }

  public getRequiredTasks(): Task[] {
    return this.tasks.filter((task) => !task.optional)
  }

  public hasTasks(): boolean {
    return Array.isArray(this.tasks) && this.tasks.length > 0
  }

  /**
   * Returns the path to the task with the given id inside the tree in the form of an array,
   * with this PathNode as the first element and the Task as the last one (if the task is in the tree),
   * or it will return null if the task cannot be found in the tree.
   *
   * @param taskId Id of the task to find the path in the tree for
   * @param pathIdHints (optional) An array with child PathNode ids (in order) that should be used to determine/verify
   * the path. If the pathIdHints are specified, but cannot be found in the tree,  the result will be null, even if the
   * task with the given taskId exists in the tree.
   *
   * @return An array with PathNode (starting with this one) and ending with the Task with the given taskId; null if the
   *  task does not exist.
   */
  public getTaskTreePath(taskId: string, pathIdHints: string[] = []): Array<PathNode | Task> {
    let childTaskTreePath = null
    if (pathIdHints.length > 0) {
      const childNode = this.getChildNode(pathIdHints[0])
      if (typeof childNode !== 'undefined') {
        childTaskTreePath = childNode.getTaskTreePath(taskId, pathIdHints.slice(1))
      }
    } else {
      const task = this.tasks.find((_task) => _task.id === taskId)
      if (task) {
        childTaskTreePath = [task]
      } else {
        this.pathNodes.forEach((childNode) => {
          const childTreePath = childNode.getTaskTreePath(taskId)
          if (childTreePath !== null && childTreePath.length > 0) {
            childTaskTreePath = childTreePath
          }
        })
      }
    }
    return childTaskTreePath instanceof Array ? [this].concat(childTaskTreePath) : null
  }

  public clone(options?: {exclude?: string[], include?: string[], shallow?: string[]}) {
    return this._objectHelper.clone<PathNode>(PathNode, this, {
      include: ['title', 'description', 'nodeType', 'pathNodes', 'tasks'],
      shallow: ['tasks']
    }, options)
  }

  private getNormalizedTitle() {
    const matchNonAlphaNumericOrSpace = /[^a-z0-9\s]/gi
    return (this.title || '').replace(matchNonAlphaNumericOrSpace, '').trim().replace(/ +/, '-').toLowerCase()
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/models/path-node/path-node.ts